import React, { useEffect, useRef, useState } from "react";
import { api, waLink, WHATSAPP_NUMBER } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  CheckCircle2, MessageCircle, Clock, Check, Sparkles, Crown, HelpCircle, ArrowRight,
  ShieldCheck, Compass, FileText, Lock, Globe, Zap, CalendarClock, Star,
  UploadCloud, ChevronLeft, ChevronRight, QrCode, Landmark, Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Seo from "../lib/Seo";

/* ------------------------------------------------------------------ utils */
const format = (d) => d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}` : "";
// Parse "YYYY-MM-DD" as a *local* date (avoids UTC parse shifts).
const parseISODate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const PLAN_ICON = {
  one_question: HelpCircle,
  detailed_horoscope: Sparkles,
  premium_life_guidance: Crown,
};

const WHY_US = [
  { icon: ShieldCheck, title: "10+ Years of Experience", desc: "Decade of classical Jyotish practice with thousands of consultations." },
  { icon: Compass, title: "Personalized Horoscope Analysis", desc: "Chart read individually — never templated or automated." },
  { icon: Sparkles, title: "Practical & Effective Remedies", desc: "Upayas that fit your lifestyle and produce measurable relief." },
  { icon: Lock, title: "Confidential Consultation", desc: "Your questions and birth data are kept strictly private." },
  { icon: FileText, title: "Detailed PDF Recommendations", desc: "A written summary and remedies delivered after every session." },
  { icon: Globe, title: "Online Consultations Worldwide", desc: "Zoom / Google Meet sessions, available globally with recording." },
];

const HOW_STEPS = [
  { icon: CalendarClock, title: "Book Your Consultation", desc: "Choose a plan and a time slot that works for you." },
  { icon: FileText, title: "Share Birth Details", desc: "Send accurate date, time and place of birth via the form." },
  { icon: ShieldCheck, title: "Secure Online Payment", desc: "Pay via UPI, Google Pay, PhonePe or bank transfer." },
  { icon: Sparkles, title: "Attend Your Consultation", desc: "Meet online, get answers, and receive a written PDF summary." },
];

const FAQS = [
  { q: "How do online consultations work?", a: "Sessions are conducted online via Zoom or Google Meet. A meeting link is shared after payment confirmation. A recording of the session and a written PDF summary are delivered within 48 hours." },
  { q: "What birth details are required?", a: "Accurate date, time (as precise as possible) and place of birth. If your time is uncertain, we offer birth-time rectification as an add-on." },
  { q: "Can I ask additional questions?", a: "Yes — each plan includes a defined number of questions. For deeper guidance, choose the Detailed or Premium plan, or upgrade before your session." },
  { q: "Will I receive remedies?", a: "Yes. Every consultation includes classical remedies — mantras, yantras, gemstone or Rudraksha suggestions, donations and rituals — chosen specifically for your chart." },
  { q: "Can consultations be rescheduled?", a: "Absolutely. Requests made 24 hours before the appointment can be rescheduled once at no cost." },
  { q: "Are consultations confidential?", a: "Yes — your birth data, questions and recording are treated with complete confidentiality and never shared." },
];

/* ------------------------------------------ Testimonial slider component */
function TestimonialSlider({ items }) {
  const [i, setI] = useState(0);
  const count = items.length;
  const prev = () => setI((v) => (v - 1 + count) % count);
  const next = () => setI((v) => (v + 1) % count);

  if (count === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-foreground/20 p-14 text-center text-foreground/60">
        <Star className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
        <div className="font-serif text-2xl">Your review could be featured here.</div>
        <div className="text-sm mt-2">Share your experience with YourAstroHub on WhatsApp after your session.</div>
      </div>
    );
  }
  const t = items[i];
  return (
    <div>
      <div className="rounded-3xl border border-foreground/10 bg-background p-8 md:p-12">
        <div className="flex gap-1 text-[#D4AF37] mb-5">
          {[...Array(t.rating)].map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}
        </div>
        <p className="font-serif text-2xl md:text-3xl leading-snug text-foreground/90">&ldquo;{t.quote}&rdquo;</p>
        <div className="border-t border-foreground/10 pt-5 mt-8">
          <div className="font-semibold text-lg">{t.name}</div>
          <div className="text-xs text-foreground/60 mt-1">{t.role}{t.location ? ` · ${t.location}` : ""}</div>
        </div>
      </div>
      {count > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex gap-1.5">
            {items.map((_, k) => (
              <button key={k} onClick={() => setI(k)} data-testid={`c-dot-${k}`} aria-label={`Review ${k+1}`}
                className={`h-1.5 rounded-full transition-all ${k === i ? "w-8 bg-[#D4AF37]" : "w-3 bg-foreground/20 hover:bg-foreground/40"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={prev} data-testid="c-prev" aria-label="Previous" className="w-11 h-11 rounded-full border border-foreground/15 hover:border-[#D4AF37] grid place-items-center"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={next} data-testid="c-next" aria-label="Next" className="w-11 h-11 rounded-full border border-foreground/15 hover:border-[#D4AF37] grid place-items-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------ Cosmic backdrop for hero */
function CosmicBackdrop() {
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A] via-[#0B1F3A] to-[#062036]" />
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#0E9F6E]/12 blur-[120px]" />
      <div className="absolute -bottom-32 right-0 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
      {[...Array(38)].map((_, i) => (
        <span key={i} className="star" style={{
          width: `${Math.random() * 2 + 1}px`,
          height: `${Math.random() * 2 + 1}px`,
          left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 4}s`,
        }} />
      ))}
      {/* subtle zodiac ring right */}
      <svg viewBox="0 0 500 500" className="absolute -right-40 top-10 w-[620px] opacity-15 wheel-spin">
        <circle cx="250" cy="250" r="220" fill="none" stroke="#D4AF37" strokeWidth="1.2" strokeDasharray="2 6" />
        <circle cx="250" cy="250" r="180" fill="none" stroke="#D4AF37" strokeOpacity="0.7" />
        <circle cx="250" cy="250" r="140" fill="none" stroke="#10B981" strokeOpacity="0.5" />
      </svg>
    </div>
  );
}

/* ============================================================ MAIN PAGE */
export default function Consultations() {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [config, setConfig] = useState({ time_slots: [], lead_days: 8, min_bookable_date: null });
  const [minDate, setMinDate] = useState(addDays(new Date(), 8));
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [fullyBookedDates, setFullyBookedDates] = useState([]); // array of Date
  const [availLoading, setAvailLoading] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", dob: "", tob: "", pob: "", notes: "" });
  const [birthChart, setBirthChart] = useState(null); // { name, base64 }
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const formRef = useRef(null);

  // SEO
  useEffect(() => {
    document.title = "Book Vedic Astrology Consultation Online | YourAstroHub";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Book a personal Vedic astrology consultation with expert Jyotishi Bhupaal Bisht. Career, marriage, business, health, finance, foreign travel, childbirth, property and spiritual guidance. Online, confidential, worldwide.");
    const kw = document.querySelector('meta[name="keywords"]');
    if (kw) kw.setAttribute("content", "Vedic Astrology Consultation, Online Astrologer India, Horoscope Reading, Career Astrology, Marriage Astrology, Astrology Consultation Online, Vedic Jyotish Expert, Bhupaal Bisht");
    return () => { document.title = "YourAstroHub — Decode Your Destiny with Vedic Wisdom"; };
  }, []);

  useEffect(() => {
    api.get("/consultations/categories").then((r) => setCategories(r.data.categories));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/consultations/config").then((r) => {
      const cfg = r.data || {};
      setConfig(cfg);
      const md = cfg.min_bookable_date ? parseISODate(cfg.min_bookable_date) : addDays(new Date(), cfg.lead_days || 8);
      setMinDate(md);
      setDate(md);
    }).catch(() => {
      const md = addDays(new Date(), 8);
      setMinDate(md);
      setDate(md);
    });
  }, []);

  // Fetch availability for a 120-day window starting from minDate so the
  // calendar can grey / mark fully-booked dates.
  const refreshAvailability = React.useCallback(async () => {
    if (!minDate) return;
    setAvailLoading(true);
    const from = format(minDate);
    const to = format(addDays(minDate, 120));
    try {
      const { data } = await api.get(`/consultations/availability?from=${from}&to=${to}`);
      const dates = (data.fully_booked_dates || []).map(parseISODate).filter(Boolean);
      setFullyBookedDates(dates);
    } catch {
      setFullyBookedDates([]);
    } finally {
      setAvailLoading(false);
    }
  }, [minDate]);

  useEffect(() => { refreshAvailability(); }, [refreshAvailability]);

  useEffect(() => {
    if (!date) return;
    setSlot("");
    api.get(`/consultations/slots?date=${format(date)}`).then((r) => setSlots(r.data.slots));
  }, [date]);

  useEffect(() => {
    const onScroll = () => setShowStickyCta(window.scrollY > 700);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const selectedCat = categories.find((c) => c.key === category);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pickPlan = (key) => {
    setCategory(key);
    setTimeout(scrollToForm, 60);
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("File must be under 2 MB.");
      return;
    }
    const b = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(f);
    });
    setBirthChart({ name: f.name, base64: String(b) });
    toast.success("Birth chart attached");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!category) { toast.error("Please choose a consultation plan first."); return; }
    if (!date) { toast.error("Please pick a date at least 8 days from today."); return; }
    if (!slot) { toast.error("Please select a time slot."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/consultations", {
        ...form,
        category,
        date: format(date),
        slot,
        birth_chart_name: birthChart?.name || null,
        birth_chart_data: birthChart?.base64 || null,
      });
      setConfirmation(data);
      refreshAvailability();
      toast.success("Appointment requested. We will confirm on WhatsApp soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed.");
    } finally { setBusy(false); }
  };

  // Booking-day computed flags
  const dateISO = date ? format(date) : "";
  const isSelectedDateFullyBooked = fullyBookedDates.some((d) => format(d) === dateISO);
  const availableSlotCount = slots.filter((s) => s.available).length;

  if (confirmation) {
    const cat = categories.find((c) => c.key === confirmation.category);
    return (
      <div data-testid="consultation-confirmation" className="min-h-[70vh] grid place-items-center px-6 py-24">
        <div className="max-w-lg w-full rounded-3xl border border-[#D4AF37]/40 p-10 text-center bg-background">
          <div className="w-14 h-14 rounded-full grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 mx-auto mb-5">
            <CheckCircle2 className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h2 className="font-serif text-3xl mb-2">Appointment Requested</h2>
          <p className="text-foreground/70 mb-6">
            Thank you {confirmation.name}. Your <b>{cat?.name || confirmation.category}</b> is tentatively scheduled for <b>{confirmation.date}</b> at <b>{confirmation.slot}</b>. We will confirm on WhatsApp after payment.
          </p>
          <p className="text-sm text-foreground/60 mb-6">Reference: <code className="text-[#D4AF37]">{confirmation.id.slice(0,8)}</code></p>
          <a href={waLink(`Hi, I have requested a ${cat?.name || confirmation.category} consultation on ${confirmation.date} at ${confirmation.slot}. My reference is ${confirmation.id.slice(0,8)}. Please share payment details.`)} target="_blank" rel="noreferrer">
            <Button className="rounded-full bg-[#25D366] hover:bg-[#20b957] text-white"><MessageCircle className="w-4 h-4 mr-2" />Continue on WhatsApp</Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="consultations-page" className="min-h-screen">
      <Seo
        title="Book Vedic Astrology Consultation Online — Career, Marriage, Health, Business | YourAstroHub"
        description="Book a personal Vedic astrology consultation with Bhupaal Bisht — accurate horoscope reading for career, marriage, business, health, finance, education, foreign travel, childbirth, property and spiritual growth."
        keywords="Online Astrology Consultation, Horoscope Consultation, Career Prediction, Marriage Prediction, Health Astrology, Finance Astrology, Business Astrology, Birth Chart Analysis, Vedic Astrologer, Bhupaal Bisht"
        path="/consultations"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Consultations", path: "/consultations" },
        ]}
        schemas={[{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }, {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Vedic Astrology Consultation",
          provider: { "@type": "Organization", name: "YourAstroHub" },
          areaServed: "Worldwide (Online)",
          offers: (categories || []).map((c) => ({
            "@type": "Offer",
            name: c.name,
            price: c.fees,
            priceCurrency: "INR",
            description: c.tagline,
          })),
        }]}
      />
      {/* HERO -------------------------------------------------------- */}
      <section className="relative overflow-hidden -mt-20 pt-32 md:pt-40 pb-24 md:pb-32 text-[#F5E8D8] bg-[#0B1F3A]">
        <CosmicBackdrop />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-8 text-center">
          <div className="reveal eyebrow flex items-center justify-center gap-3 mb-6">
            <span className="w-8 gold-hairline" /> Online Vedic Consultation <span className="w-8 gold-hairline" />
          </div>
          <h1 data-testid="booking-heading" className="reveal-2 font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.05]">
            Book Your Personal <span className="gold-text italic">Astrology Consultation</span>
          </h1>
          <p className="reveal-3 mt-6 text-base md:text-lg text-[#F5E8D8]/75 max-w-3xl mx-auto leading-relaxed">
            Receive accurate Vedic astrology guidance for <b>Career, Marriage, Business, Health, Finance, Education,
            Foreign Travel, Childbirth, Property, and Spiritual Growth</b> through personalized horoscope analysis.
          </p>
          <div className="reveal-4 mt-9 flex flex-wrap gap-3 justify-center">
            <a href={waLink("Hello, I would like to book an astrology consultation with YourAstroHub.")} target="_blank" rel="noreferrer" data-testid="hero-wa-cta">
              <Button size="lg" className="rounded-full bg-[#25D366] hover:bg-[#20b957] text-white font-semibold px-8 py-6">
                <MessageCircle className="w-4 h-4 mr-2" />Book on WhatsApp
              </Button>
            </a>
            <button onClick={scrollToForm} data-testid="hero-schedule-cta">
              <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                Schedule Appointment <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </button>
          </div>
        </div>
      </section>

      {/* WHY US ------------------------------------------------------ */}
      <section data-testid="why-us" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="eyebrow mb-3">Why Choose YourAstroHub?</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">
              Guidance that <span className="gold-text italic">respects your time.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_US.map((w, i) => (
              <div key={w.title} data-testid={`why-${i}`} className="group p-7 rounded-3xl bg-background border border-foreground/10 hover:border-[#D4AF37]/60 transition-all hover:-translate-y-1 hover:shadow-xl">
                <div className="w-12 h-12 rounded-2xl grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-5 group-hover:bg-[#10B981]/15 group-hover:border-[#10B981]/40 transition-colors">
                  <w.icon className="w-5 h-5 text-[#D4AF37] group-hover:text-[#0E9F6E] transition-colors" />
                </div>
                <h3 className="font-serif text-xl md:text-2xl leading-tight mb-2">{w.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLAN CARDS -------------------------------------------------- */}
      <section data-testid="plans-section" className="py-24 bg-[#F4EFE6] dark:bg-[#0F284B]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="eyebrow mb-3">Consultation Plans</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">
              Three paths. <span className="emerald-text italic">One trusted mentor.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {categories.map((c) => {
              const Icon = PLAN_ICON[c.key] || Sparkles;
              const selected = category === c.key;
              const popular = c.popular;
              const btnLabel = c.key === "premium_life_guidance" ? "Book Premium Consultation" : "Book Now";
              return (
                <div key={c.key} data-testid={`plan-${c.key}`}
                  className={`relative rounded-3xl p-8 md:p-10 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                    popular
                      ? "bg-[#0B1F3A] text-[#F5E8D8] border-2 border-[#D4AF37] shadow-2xl md:scale-[1.03]"
                      : "bg-background border border-foreground/10 hover:border-[#D4AF37]/60"
                  } ${selected ? "ring-2 ring-[#D4AF37] ring-offset-2 ring-offset-background" : ""}`}>
                  {popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-[#D4AF37] text-[#0B1F3A] text-[10px] uppercase tracking-[0.24em] font-bold shadow-lg">
                      Most Popular
                    </div>
                  )}
                  <div className={`w-14 h-14 rounded-2xl grid place-items-center mb-6 ${popular ? "bg-[#D4AF37]/20 border border-[#D4AF37]/50" : "bg-[#D4AF37]/10 border border-[#D4AF37]/30"}`}>
                    <Icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-serif text-2xl leading-tight mb-2">{c.name}</h3>
                  <div className={`text-xs uppercase tracking-[0.22em] flex items-center gap-1.5 mb-6 ${popular ? "text-[#F5E8D8]/70" : "text-foreground/60"}`}>
                    <Clock className="w-3.5 h-3.5" />{c.duration}
                  </div>
                  <div className="mb-6">
                    <div className="font-serif text-5xl text-[#D4AF37] leading-none">₹{c.fees.toLocaleString()}</div>
                    <div className={`text-xs mt-2 ${popular ? "text-[#F5E8D8]/60" : "text-foreground/60"}`}>{c.tagline}</div>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {c.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${popular ? "text-[#D4AF37]" : "text-[#AD974F]"}`} />
                        <span className={popular ? "text-[#F5E8D8]/90" : "text-foreground/80"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button data-testid={`plan-book-${c.key}`} onClick={() => pickPlan(c.key)} size="lg"
                    className={`w-full rounded-full font-semibold py-6 ${
                      popular
                        ? "bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A]"
                        : "bg-[#0B1F3A] hover:bg-[#0F284B] text-[#F5E8D8] dark:bg-[#D4AF37] dark:hover:bg-[#AD974F] dark:text-[#0B1F3A]"
                    }`}>
                    {btnLabel} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS ------------------------------------------------ */}
      <section data-testid="how-it-works" className="py-24 md:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="eyebrow mb-3">How It Works</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Four simple <span className="gold-text italic">steps.</span></h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5 relative">
            {HOW_STEPS.map((s, i) => (
              <div key={s.title} data-testid={`step-${i+1}`} className="relative p-7 rounded-3xl border border-foreground/10 bg-background hover:border-[#10B981]/60 transition-colors">
                <div className="absolute -top-4 -left-1 text-[6rem] font-serif text-[#D4AF37]/10 leading-none pointer-events-none select-none">{String(i+1).padStart(2,"0")}</div>
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl grid place-items-center bg-[#10B981]/10 border border-[#10B981]/30 mb-5">
                    <s.icon className="w-5 h-5 text-[#0E9F6E]" />
                  </div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Step {i+1}</div>
                  <h3 className="font-serif text-xl leading-tight mb-2">{s.title}</h3>
                  <p className="text-sm text-foreground/70">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING FORM ------------------------------------------------ */}
      <section id="booking-form" ref={formRef} data-testid="booking-form-section" className="py-24 bg-[#F4EFE6] dark:bg-[#0F284B]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="eyebrow mb-3">Appointment Booking</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">
              {selectedCat ? <>You picked <span className="gold-text italic">{selectedCat.name}</span></> : "Complete your booking"}
            </h2>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            {/* Left: date + slots */}
            <div className="lg:col-span-5 space-y-5">
              <div className="rounded-3xl border border-foreground/10 bg-background p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="eyebrow">Preferred Date</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">
                    IST · 8-day advance booking
                  </div>
                </div>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  disabled={{ before: minDate }}
                  defaultMonth={minDate || undefined}
                  modifiers={{ fullyBooked: fullyBookedDates }}
                  modifiersClassNames={{
                    fullyBooked: "line-through text-rose-500 bg-rose-500/10 hover:bg-rose-500/15",
                  }}
                  className="rounded-2xl"
                  data-testid="booking-calendar"
                />
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[11px] text-foreground/60">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-[#D4AF37]" /> Selected
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-rose-500/30 border border-rose-500/60" /> Fully booked
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-foreground/10" /> Not yet bookable
                  </span>
                </div>
                {minDate && (
                  <div className="mt-3 text-xs text-foreground/60" data-testid="booking-min-date-hint">
                    Earliest available date: <b className="text-foreground/80">{minDate.toDateString()}</b>
                  </div>
                )}
              </div>
              <div className="rounded-3xl border border-foreground/10 bg-background p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="eyebrow">Preferred Time Slot</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">IST</div>
                </div>
                {isSelectedDateFullyBooked || (slots.length > 0 && availableSlotCount === 0) ? (
                  <div data-testid="date-fully-booked" className="p-5 rounded-2xl border border-rose-500/40 bg-rose-500/5 text-center">
                    <div className="font-serif text-lg text-rose-600 dark:text-rose-400">Fully Booked</div>
                    <div className="text-sm text-foreground/70 mt-1">
                      All three slots on <b>{date ? date.toDateString() : "this date"}</b> are already reserved.
                      Please pick another date on the calendar.
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {slots.map((s, idx) => (
                      <button key={s.slot} type="button" data-testid={`slot-${idx + 1}`}
                        disabled={!s.available}
                        onClick={() => setSlot(s.slot)}
                        className={`p-3.5 rounded-2xl text-sm font-medium border transition-all ${
                          slot === s.slot ? "bg-[#D4AF37] text-[#0B1F3A] border-[#D4AF37] shadow-sm" :
                          s.available ? "border-foreground/15 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5" :
                          "opacity-40 line-through cursor-not-allowed border-foreground/10 bg-foreground/5"
                        }`}>
                        <span className="inline-flex items-center gap-2 justify-center">
                          <Clock className="w-3.5 h-3.5" />
                          {s.slot}
                          {!s.available && <span className="text-[10px] uppercase tracking-widest ml-1">Booked</span>}
                        </span>
                      </button>
                    ))}
                    {slots.length === 0 && (
                      <div className="text-sm text-foreground/50 text-center py-4">Select a date to see slot availability.</div>
                    )}
                  </div>
                )}
                {availLoading && (
                  <div className="mt-3 text-[11px] text-foreground/50 text-center">Refreshing availability…</div>
                )}
              </div>
            </div>

            {/* Right: form */}
            <form onSubmit={submit} className="lg:col-span-7 rounded-3xl border border-foreground/10 bg-background p-8 space-y-5 h-fit">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label htmlFor="name">Full Name</Label><Input id="name" data-testid="booking-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="rounded-full mt-2" /></div>
                <div><Label htmlFor="phone">Mobile Number</Label><Input id="phone" data-testid="booking-phone" required value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="rounded-full mt-2" placeholder="+91 ..." /></div>
                <div className="md:col-span-2"><Label htmlFor="email">Email Address</Label><Input id="email" data-testid="booking-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="rounded-full mt-2" /></div>
                <div><Label htmlFor="dob">Date of Birth</Label><Input id="dob" data-testid="booking-dob" type="date" value={form.dob} onChange={(e)=>setForm({...form, dob:e.target.value})} className="rounded-full mt-2" /></div>
                <div><Label htmlFor="tob">Time of Birth</Label><Input id="tob" data-testid="booking-tob" type="time" value={form.tob} onChange={(e)=>setForm({...form, tob:e.target.value})} className="rounded-full mt-2" /></div>
                <div className="md:col-span-2"><Label htmlFor="pob">Place of Birth</Label><Input id="pob" data-testid="booking-pob" value={form.pob} onChange={(e)=>setForm({...form, pob:e.target.value})} placeholder="City, Country" className="rounded-full mt-2" /></div>
                <div className="md:col-span-2">
                  <Label>Select Consultation Plan</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="booking-plan-select" className="rounded-full mt-2"><SelectValue placeholder="Choose a consultation plan" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.key} value={c.key}>{c.name} — ₹{c.fees.toLocaleString()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Questions You Want to Discuss</Label>
                <Textarea id="notes" data-testid="booking-notes" rows={4} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="rounded-2xl mt-2" placeholder="Share what you would like to explore in the session." />
              </div>

              <div>
                <Label>Upload Birth Chart (optional)</Label>
                <label className="mt-2 flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-foreground/20 hover:border-[#D4AF37] cursor-pointer transition-colors">
                  <div className="w-10 h-10 rounded-full grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                    <UploadCloud className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">{birthChart ? birthChart.name : "Click to upload birth chart"}</div>
                    <div className="text-xs text-foreground/60">PDF / JPG / PNG · Max 2 MB</div>
                  </div>
                  <input type="file" data-testid="booking-birth-chart" accept="application/pdf,image/*" className="hidden" onChange={onFilePick} />
                </label>
              </div>

              <div className="rounded-2xl bg-[#F4EFE6] dark:bg-[#0F284B] p-5 flex flex-wrap gap-4 justify-between items-center">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-foreground/60">Booking Summary</div>
                  <div className="mt-1 font-serif text-lg">{selectedCat?.name || "— Choose a plan —"}</div>
                  <div className="text-sm text-foreground/70">{date ? date.toDateString() : "Pick a date"} · {slot || "no slot"}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-foreground/60">Session Fee</div>
                  <div className="font-serif text-3xl text-[#D4AF37]">{selectedCat ? `₹${selectedCat.fees.toLocaleString()}` : "—"}</div>
                  <div className="text-[10px] text-foreground/50 mt-1">Payment via UPI / Bank</div>
                </div>
              </div>

              <Button type="submit" data-testid="booking-submit" disabled={busy || !category} size="lg"
                className="w-full rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold py-6 disabled:opacity-50">
                {busy ? "Submitting..." : "Request Appointment"}
              </Button>

              <div className="text-center text-xs text-foreground/60">
                Prefer chat? <a href={waLink()} target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:underline">WhatsApp support</a> · +91 92892 12630
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* PAYMENT SECTION -------------------------------------------- */}
      <section data-testid="payment-section" className="py-24 bg-background">
        <div className="max-w-6xl mx-auto px-6 md:px-8 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="eyebrow mb-3">Secure Payment</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight">
              Pay securely. <span className="emerald-text italic">Book with confidence.</span>
            </h2>
            <p className="mt-5 text-foreground/70 leading-relaxed max-w-lg">
              Choose any of India&rsquo;s most trusted payment options. Payment details will be shared on WhatsApp
              after appointment confirmation.
            </p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: Smartphone, label: "UPI" },
                { icon: Zap, label: "Google Pay" },
                { icon: Sparkles, label: "PhonePe" },
                { icon: Landmark, label: "Bank Transfer" },
              ].map((p) => (
                <div key={p.label} data-testid={`pay-${p.label.toLowerCase().replace(/\s+/g,'-')}`} className="p-4 rounded-2xl border border-foreground/10 flex flex-col items-center text-center gap-2 hover:border-[#D4AF37]/60 transition-colors">
                  <div className="w-10 h-10 rounded-full grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                    <p.icon className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <div className="text-sm font-medium">{p.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="rounded-3xl border border-[#D4AF37]/40 p-8 bg-background text-center">
              <div className="eyebrow mb-4">Scan to Pay</div>
              <div className="mx-auto w-48 h-48 rounded-2xl border-2 border-dashed border-foreground/20 bg-[#F4EFE6] dark:bg-[#0F284B] grid place-items-center">
                <div className="flex flex-col items-center gap-2 text-foreground/50">
                  <QrCode className="w-10 h-10" />
                  <span className="text-xs uppercase tracking-[0.22em]">QR placeholder</span>
                </div>
              </div>
              <div className="mt-5 text-sm text-foreground/70">Your UPI QR code will appear here.</div>
              <div className="mt-2 text-xs text-foreground/50">Share your admin dashboard update to enable it.</div>
              <a href={waLink("Hi, please share the UPI / payment details for the consultation booking.")} target="_blank" rel="noreferrer" className="inline-block mt-5">
                <Button variant="outline" className="rounded-full border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10">
                  <MessageCircle className="w-4 h-4 mr-2" />Get Payment Details
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PAYMENT SECTION -------------------------------------------- */}
      <section data-testid="payment-section" className="py-24 bg-background">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="rounded-3xl border-2 border-[#D4AF37]/40 bg-[#0B1F3A] text-[#F5E8D8] p-8 md:p-12 grid md:grid-cols-[auto_1fr_auto] gap-6 items-center relative overflow-hidden grain">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#10B981]/25 blur-3xl" />
            <div className="relative w-14 h-14 rounded-2xl grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40">
              <QrCode className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div className="relative">
              <div className="eyebrow mb-2">Next Step</div>
              <h3 className="font-serif text-2xl md:text-3xl leading-tight">Complete your payment securely</h3>
              <p className="text-sm text-[#F5E8D8]/70 mt-2">Pay via UPI, Google Pay, Paytm, PhonePe or bank transfer. Upload your payment proof to activate your booking.</p>
            </div>
            <Link to="/payment" data-testid="go-to-payment" className="relative">
              <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                Go to Payment <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ --------------------------------------------------------- */}
      <section data-testid="faq" className="py-24 bg-[#F4EFE6] dark:bg-[#0F284B]">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <div className="eyebrow mb-3">Frequently Asked Questions</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Everything you need to <span className="gold-text italic">know.</span></h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-foreground/10">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-serif text-lg md:text-xl py-5 hover:text-[#D4AF37] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-foreground/75 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* TESTIMONIALS SLIDER ---------------------------------------- */}
      <section data-testid="testimonials-slider" className="py-24 bg-background">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <div className="eyebrow mb-3">Words from Our Seekers</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Voices of <span className="emerald-text italic">quiet transformation.</span></h2>
          </div>
          <TestimonialSlider items={testimonials} />
        </div>
      </section>

      {/* FINAL CTA -------------------------------------------------- */}
      <section data-testid="final-cta" className="pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 bg-[#0B1F3A] text-[#F5E8D8] grain text-center">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#10B981]/25 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#D4AF37]/25 blur-3xl rounded-full" />
            <div className="relative">
              <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight max-w-3xl mx-auto">
                Your Horoscope Holds the Answers. <br className="hidden md:block" />
                <span className="gold-text italic">Let&rsquo;s Discover Them Together.</span>
              </h3>
              <div className="mt-9 flex flex-wrap gap-3 justify-center">
                <a href={waLink("Hello, I would like to book an astrology consultation with YourAstroHub.")} target="_blank" rel="noreferrer" data-testid="final-wa">
                  <Button size="lg" className="rounded-full bg-[#25D366] hover:bg-[#20b957] text-white font-semibold px-8 py-6">
                    <MessageCircle className="w-4 h-4 mr-2" />Book on WhatsApp
                  </Button>
                </a>
                <button onClick={scrollToForm} data-testid="final-schedule">
                  <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                    Schedule Consultation <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STICKY BOOK BUTTON (mobile + desktop) */}
      {showStickyCta && (
        <button
          onClick={scrollToForm}
          data-testid="sticky-book-cta"
          className="fixed bottom-24 md:bottom-6 left-6 z-40 group"
          aria-label="Book Consultation"
        >
          <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-[#D4AF37] text-[#0B1F3A] font-semibold shadow-2xl border border-[#0B1F3A]/10 hover:bg-[#AD974F] transition-all">
            <CalendarClock className="w-4 h-4" />Book Consultation
          </div>
        </button>
      )}
    </div>
  );
}
